import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, User, LogIn } from "lucide-react";
import { navLinks } from "../mock";
import { useUserAuth } from "../contexts/UserAuthContext";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user } = useUserAuth();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-[#07111F]/95 backdrop-blur-xl shadow-xl"
          : "bg-[#0F1B2E]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-3 flex items-center justify-between gap-3">

        {/* Logo */}
        <Link to="/" className="flex items-center group shrink-0" aria-label="Train Driver Foundation home">
          <img
            src="/logo-96.webp"
            alt="Train Driver Foundation"
            width="48"
            height="48"
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-2 ring-white/10 group-hover:ring-[#E63B2E]/60 transition-all"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-2">
          {navLinks.slice(0, 8).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? "text-white bg-[#E63B2E] shadow-md"
                    : "text-slate-200 hover:text-white hover:bg-white/10"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <Link
              to="/my-account"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 px-3.5 py-2 text-xs font-bold text-white transition whitespace-nowrap"
              data-testid="header-my-account-link"
              title={user.email}
            >
              <User className="w-3.5 h-3.5" /> My account
            </Link>
          ) : (
            <Link
              to="/auth/login"
              className="inline-flex items-center gap-1.5 rounded-full text-white/80 hover:text-white px-3 py-2 text-xs font-bold transition whitespace-nowrap"
              data-testid="header-signin-link"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign in
            </Link>
          )}
          <Link
            to="/complete-career-package"
            className="ml-1 inline-flex items-center gap-2 rounded-full bg-[#E63B2E] px-5 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-[#c92f24] hover:shadow-xl transition-all whitespace-nowrap"
          >
            Full Preparation
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          aria-label="Open menu"
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-open"
          className="lg:hidden w-11 h-11 rounded-full bg-white text-[#0F1B2E] flex items-center justify-center shadow-md hover:bg-slate-100 transition shrink-0"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          <aside className="absolute right-0 top-0 h-full w-[88%] max-w-sm bg-[#0F1B2E] shadow-2xl flex flex-col border-l border-white/10">
            {/* Drawer header (sticky) */}
            <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
              <Link to="/" className="flex items-center gap-3 min-w-0">
                <img
                  src="/logo-96.webp"
                  alt="Train Driver Foundation"
                  width="40"
                  height="40"
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-white/10 shrink-0"
                />
                <div className="leading-tight min-w-0">
                  <p className="text-white font-extrabold text-base truncate">TDF</p>
                  <p className="text-slate-300 text-xs truncate">
                    Rail career support
                  </p>
                </div>
              </Link>

              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-5 py-5" style={{ WebkitOverflowScrolling: "touch" }}>
              <nav className="flex flex-col gap-1.5">
                {navLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-xl text-base font-semibold transition-all ${
                        isActive
                          ? "bg-[#E63B2E] text-white shadow-md"
                          : "text-slate-200 hover:bg-white/10 hover:text-white active:bg-white/10"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </nav>

              <Link
                to="/complete-career-package"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#E63B2E] px-5 py-3 text-base font-bold text-white shadow-lg hover:bg-[#c92f24] transition"
              >
                Full Preparation
                <ArrowRight className="w-4 h-4" />
              </Link>

              <Link
                to={user ? "/my-account" : "/auth/login"}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 px-5 py-3 text-base font-bold text-white transition"
                data-testid="mobile-account-link"
              >
                {user ? <><User className="w-4 h-4" /> My account</> : <><LogIn className="w-4 h-4" /> Sign in</>}
              </Link>

              <div className="mt-8 pt-5 text-xs text-slate-400 border-t border-white/10">
                <p>UK TRAIN DRIVER ACADEMY LTD</p>
                <p>Company No. 13240841</p>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Red line */}
      <div className="h-[3px] w-full bg-[#E63B2E] shadow-[0_0_10px_rgba(230,59,46,0.5)]" />
    </header>
  );
}